import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

export type AgentDowngradeTarget = { name: string; agentVersion?: string };

const LISTED_TARGETS = 5;

// Downgrading the agent on Windows can delete the agent exe and leave the
// device unable to connect (ID-7350), so a downgrade asks for an explicit
// acknowledgement before it is deployed.
export const AgentDowngradeWarning = ({
  version,
  targets,
  acknowledged,
  onAcknowledgedChange,
}: {
  version?: string;
  // The targets that run a newer or unknown agent, or undefined when the
  // targets themselves are unknown.
  targets?: AgentDowngradeTarget[];
  acknowledged: boolean;
  onAcknowledgedChange: (acknowledged: boolean) => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <h1>{t("agentDowngradeTitle", { version })}</h1>
      <div className="border p-2 rounded-md mb-2 border-destructive bg-destructive/10 text-destructive space-y-2">
        <div className="font-bold">
          {targets
            ? t("agentDowngradeOlderThanTargets", { version })
            : t("agentDowngradeTargetsUnknown", { version })}
        </div>
        {targets && (
          <ul className="list-disc ml-5">
            {targets.slice(0, LISTED_TARGETS).map((target) => (
              <li key={target.name}>
                {target.name} (
                {target.agentVersion ?? t("agentDowngradeUnknownVersion")})
              </li>
            ))}
            {targets.length > LISTED_TARGETS && (
              <li>
                {t("agentDowngradeMoreTargets", {
                  count: targets.length - LISTED_TARGETS,
                })}
              </li>
            )}
          </ul>
        )}
        <div>{t("agentDowngradeWindowsRisk")}</div>
      </div>
      <div className="flex items-start gap-2">
        <Checkbox
          id="acknowledge-agent-downgrade"
          className="mt-0.5"
          checked={acknowledged}
          onCheckedChange={(c) => onAcknowledgedChange(c === true)}
        />
        <Label htmlFor="acknowledge-agent-downgrade" className="font-normal">
          {t("agentDowngradeAcknowledge")}
        </Label>
      </div>
    </div>
  );
};
